import {
  log,
  logInfo,
  logError,
  logWarn,
  captureException,
  addBreadcrumb,
} from "../utils/logger.js";

/**
 * Facebook Pixel Error Handler
 * Provides fallback mechanisms and error recovery for pixel loading failures
 */

class PixelErrorHandler {
  static errorCount = 0;
  static maxRetries = 3;
  static retryDelay = 2000; // 2 seconds
  static isRetrying = false;

  /**
   * Handle pixel initialization errors
   * @param {Error} error - The error that occurred
   * @param {Function} retryCallback - Callback to retry initialization
   * @returns {boolean} - Whether the error was handled
   */
  static handleInitError(error, retryCallback) {
    logError("Facebook Pixel initialization error:", error);

    // Add breadcrumb for Sentry
    addBreadcrumb({
      message: "Facebook Pixel initialization failed",
      category: "facebook-pixel",
      level: "error",
      data: {
        error: error.message,
        retryCount: this.errorCount,
        maxRetries: this.maxRetries,
        timestamp: new Date().toISOString(),
      },
    });

    // Capture exception in Sentry
    captureException(error, {
      component: "facebook-pixel",
      action: "initialization",
      retryCount: this.errorCount,
      maxRetries: this.maxRetries,
    });

    this.errorCount++;

    if (this.errorCount <= this.maxRetries && !this.isRetrying) {
      this.scheduleRetry(retryCallback);
      return true;
    }

    this.logFinalError("Initialization failed after maximum retries");
    return false;
  }

  /**
   * Handle pixel tracking errors
   * @param {Error} error - The error that occurred
   * @param {string} eventName - The event that failed to track
   * @param {Object} parameters - The event parameters
   * @returns {boolean} - Whether the error was handled
   */
  static handleTrackError(error, eventName, parameters) {
    logError(`Facebook Pixel track error for ${eventName}:`, error);

    // Add breadcrumb for Sentry
    addBreadcrumb({
      message: `Facebook Pixel tracking failed: ${eventName}`,
      category: "facebook-pixel",
      level: "error",
      data: {
        eventName,
        error: error.message,
        parameters,
        timestamp: new Date().toISOString(),
      },
    });

    // Capture exception in Sentry
    captureException(error, {
      component: "facebook-pixel",
      action: "tracking",
      eventName,
      parameters,
    });

    // Store failed events for potential retry
    this.storeFailedEvent(eventName, parameters);

    // Don't retry tracking errors immediately to avoid spam
    return true;
  }

  /**
   * Schedule a retry for pixel initialization
   * @param {Function} retryCallback - Callback to retry initialization
   */
  static scheduleRetry(retryCallback) {
    if (this.isRetrying) return;

    this.isRetrying = true;

    setTimeout(() => {
      logInfo(
        `Retrying Facebook Pixel initialization (attempt ${this.errorCount + 1})`
      );

      try {
        retryCallback();
        this.isRetrying = false;
        this.errorCount = 0; // Reset on success
      } catch (error) {
        this.isRetrying = false;
        this.handleInitError(error, retryCallback);
      }
    }, this.retryDelay);
  }

  /**
   * Store failed events for potential retry
   * @param {string} eventName - Event name
   * @param {Object} parameters - Event parameters
   */
  static storeFailedEvent(eventName, parameters) {
    try {
      const failedEvents = this.getFailedEvents();
      failedEvents.push({
        eventName,
        parameters,
        timestamp: Date.now(),
        retryCount: 0,
      });

      // Keep only last 50 failed events
      if (failedEvents.length > 50) {
        failedEvents.splice(0, failedEvents.length - 50);
      }

      localStorage.setItem(
        "facebookPixelFailedEvents",
        JSON.stringify(failedEvents)
      );
    } catch (error) {
      logError("Failed to store failed event:", error);
    }
  }

  /**
   * Get stored failed events
   * @returns {Array} Array of failed events
   */
  static getFailedEvents() {
    try {
      const stored = localStorage.getItem("facebookPixelFailedEvents");
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      logError("Failed to retrieve failed events:", error);
      return [];
    }
  }

  /**
   * Retry failed events when pixel is available
   * @param {Function} trackCallback - Callback to track events
   */
  static retryFailedEvents(trackCallback) {
    const failedEvents = this.getFailedEvents();

    if (failedEvents.length === 0) return;

    logInfo(`Retrying ${failedEvents.length} failed Facebook Pixel events`);

    // Add breadcrumb for retry attempt
    addBreadcrumb({
      message: `Retrying ${failedEvents.length} failed Facebook Pixel events`,
      category: "facebook-pixel",
      level: "info",
      data: {
        failedEventsCount: failedEvents.length,
        timestamp: new Date().toISOString(),
      },
    });

    failedEvents.forEach((event, index) => {
      try {
        trackCallback(event.eventName, event.parameters);

        // Add breadcrumb for successful retry
        addBreadcrumb({
          message: `Successfully retried Facebook Pixel event: ${event.eventName}`,
          category: "facebook-pixel",
          level: "info",
          data: {
            eventName: event.eventName,
            retryCount: event.retryCount,
            timestamp: new Date().toISOString(),
          },
        });

        // Remove successfully retried event
        failedEvents.splice(index, 1);
      } catch (error) {
        event.retryCount++;

        // Remove events that have been retried too many times
        if (event.retryCount >= 3) {
          failedEvents.splice(index, 1);
        }
      }
    });

    // Update stored failed events
    try {
      localStorage.setItem(
        "facebookPixelFailedEvents",
        JSON.stringify(failedEvents)
      );
    } catch (error) {
      console.error("Failed to update failed events:", error);
    }
  }

  /**
   * Log final error when all retries are exhausted
   * @param {string} message - Error message
   */
  static logFinalError(message) {
    logError(`Facebook Pixel: ${message}`);

    // Could send to analytics service or error tracking service here
    this.reportErrorToAnalytics(message);
  }

  /**
   * Report error to analytics service
   * @param {string} message - Error message
   */
  static reportErrorToAnalytics(message) {
    // This could be integrated with your existing analytics service
    logInfo("Reporting Facebook Pixel error to analytics:", message);
  }

  /**
   * Check if pixel is likely to work
   * @returns {boolean} - Whether pixel should work
   */
  static isPixelLikelyWorking() {
    return this.errorCount < this.maxRetries;
  }

  /**
   * Reset error state
   */
  static reset() {
    this.errorCount = 0;
    this.isRetrying = false;
  }

  /**
   * Get error statistics
   * @returns {Object} Error statistics
   */
  static getErrorStats() {
    return {
      errorCount: this.errorCount,
      isRetrying: this.isRetrying,
      failedEventsCount: this.getFailedEvents().length,
      isPixelLikelyWorking: this.isPixelLikelyWorking(),
    };
  }
}

export default PixelErrorHandler;
